import {ellipsis} from 'polished';
import React, {FC, Fragment, useMemo} from 'react';
import styled from 'styled-components';

import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {
  VisualConversationSenderModel,
  VisualConversationSendersModel,
} from '../../../../models/api/conversationSendersModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {makeTeammateVisualConversationSender} from '../../../../store/data/conversations/conversationSendersHelpers';
import {StyledHighlightStrong} from '../../../inboxes/cells/header/search/highlight/highlight';
import {SendersFormatterProps} from '../sendersFormatter';
import {formatSenderName} from './common';

/*
 * Styles.
 */

const StyledParticipantsWrapperDiv = styled.div<{$isCellSelected: boolean}>`
  display: flex;

  color: ${(p) => (p.$isCellSelected ? p.theme.invertedText : p.theme.greys.shade90)};
`;

const StyledParticipantNamesSpan = styled.span`
  ${ellipsis()}
  flex-shrink: 1;

  padding-left: 3px;
`;

const StyledOtherParticipantsSpan = styled.span`
  ${ellipsis()}
  flex-shrink: 0;
`;

/*
 * Component.
 */

export const DiscussionSendersFormatter: FC<SendersFormatterProps> = (props) => {
  const {conversationSenders, isCellSelected, trackerTeammates, isUserTracking, userId} = props;

  const {otherParticipantsCount, participantsToShow, isSearchMatchingOnOtherParticipants} = useMemo(
    () => computeParticipantsToShow(conversationSenders, isUserTracking, trackerTeammates, userId),
    [conversationSenders, isUserTracking, trackerTeammates, userId],
  );

  const otherParticipants = maybeRenderOtherParticipantsCount(
    otherParticipantsCount,
    isSearchMatchingOnOtherParticipants,
  );

  return (
    <StyledParticipantsWrapperDiv $isCellSelected={isCellSelected}>
      <StyledParticipantNamesSpan key="participants">
        {participantsToShow.map((item, index, array) => {
          const isNotBeforeLast = index < array.length - 1;

          return (
            <Fragment key={item.key}>
              {formatSenderName(item, isCellSelected)}
              {isNotBeforeLast && ', '}
            </Fragment>
          );
        })}
      </StyledParticipantNamesSpan>
      {otherParticipants && (
        <StyledOtherParticipantsSpan key="otherParticipants">
          , {otherParticipants}
        </StyledOtherParticipantsSpan>
      )}
    </StyledParticipantsWrapperDiv>
  );
};

function computeParticipantsToShow(
  conversationSenders: VisualConversationSendersModel,
  isUserTracking: boolean,
  trackerTeammates: ReadonlyArray<TeammateModel>,
  userId?: number,
) {
  const participantsToShow: Array<VisualConversationSenderModel> = [];

  // Add conversation senders to the participants list.
  const {firstSender, lastSender, firstOtherSender, secondOtherSender, isSearchMatchingOnOtherSenders} =
    conversationSenders;
  let isSearchMatchingOnOtherParticipants = isSearchMatchingOnOtherSenders;

  if (firstSender && senderIsParticipant(firstSender, trackerTeammates, isUserTracking)) {
    participantsToShow.push(firstSender);
  }

  if (
    lastSender &&
    // If the first and the last sender are the same, only display it once.
    firstSender?.key !== lastSender.key &&
    senderIsParticipant(lastSender, trackerTeammates, isUserTracking)
  ) {
    participantsToShow.push(lastSender);
  }

  if (firstOtherSender && senderIsParticipant(firstOtherSender, trackerTeammates, isUserTracking)) {
    participantsToShow.push(firstOtherSender);
  }

  if (secondOtherSender && senderIsParticipant(secondOtherSender, trackerTeammates, isUserTracking)) {
    if (participantsToShow.length < 3) {
      participantsToShow.push(secondOtherSender);
    } else {
      // If we'd have shown a 4th sender and it is highlighted, we should highlight the participant count instead.
      isSearchMatchingOnOtherParticipants ||= Boolean(secondOtherSender.highlight);
    }
  }

  // Remove participants that are already displayed as senders.
  const remainingParticipants = trackerTeammates.filter((teammate) =>
    participantsToShow.every((sender) => teammate.id !== sender.teammateId),
  );

  // If we have less than 3 senders and have other participants, include them.
  if (participantsToShow.length < 3 && remainingParticipants.length > 0) {
    for (let i = participantsToShow.length; i < 3; i++) {
      const participant = remainingParticipants.shift();

      if (!participant) {
        break;
      }

      participantsToShow.push(makeTeammateVisualConversationSender(participant, userId));
    }
  }

  // If we still have room for another name, try to include Me.
  let isUserShown = participantsToShow.some((participant) => participant.isMe);
  const canShowUser = isUserTracking && !isUserShown;

  if (participantsToShow.length < 3 && canShowUser) {
    // We are sure that 'Me' should not be highlighted here.
    participantsToShow.push({key: 'teammate-me', isMe: true});
    isUserShown = true;
  }

  // We can finally count the number of remaining participants.
  const otherParticipantsCount = remainingParticipants.length + Number(isUserTracking && !isUserShown);

  return {otherParticipantsCount, participantsToShow, isSearchMatchingOnOtherParticipants};
}

function maybeRenderOtherParticipantsCount(
  otherParticipantsCount: number,
  isSearchMatchingOnOtherSenders: boolean,
) {
  if (otherParticipantsCount === 0) {
    return null;
  }

  const countText = `+${otherParticipantsCount}`;

  return isSearchMatchingOnOtherSenders ? (
    <StyledHighlightStrong $backgroundColor={PaletteColorsEnum.YELLOW}>{countText}</StyledHighlightStrong>
  ) : (
    countText
  );
}

function senderIsParticipant(
  sender: VisualConversationSenderModel,
  trackerTeammates: ReadonlyArray<TeammateModel>,
  isUserTracking: boolean,
) {
  return (
    (sender.isMe && isUserTracking) || trackerTeammates.some((teammate) => teammate.id === sender.teammateId)
  );
}
